// Transcription Storage Utility using IndexedDB
import { getTranslations, getUILanguage } from './translations';

export interface TranscriptionSegment {
  text: string;
  type: 'partial' | 'final';
  segment?: number;
  start?: number;
  end?: number;
  sentence_id?: number;
}

export interface TranscriptionRecord {
  id: string;
  text: string;
  language: string;
  timestamp: Date;
  audioUrl?: string; // blob URL for audio playback (runtime only)
  duration?: number;
  isRealTime: boolean; // true for live transcription, false for file upload
  segments?: TranscriptionSegment[]; // Store original segments with timing
}

interface StoredTranscriptionRecord extends Omit<TranscriptionRecord, 'audioUrl'> {
  audioBlob?: Blob; // Store actual blob in IndexedDB
}

class TranscriptionStorage {
  private readonly DB_NAME = 'voxist_transcriptions';
  private readonly DB_VERSION = 1;
  private readonly TRANSCRIPTION_STORE = 'transcriptions';
  private readonly MAX_RECORDS = 50; // Keep last 50 transcriptions
  private blobUrls: Set<string> = new Set(); // Track created blob URLs for cleanup
  private db: IDBDatabase | null = null;

  // Initialize IndexedDB
  private async initDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;


    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create transcriptions store
        if (!db.objectStoreNames.contains(this.TRANSCRIPTION_STORE)) {
          const store = db.createObjectStore(this.TRANSCRIPTION_STORE, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  // Save transcription to IndexedDB
  async saveTranscription(transcription: Omit<TranscriptionRecord, 'id' | 'timestamp'>): Promise<TranscriptionRecord> {
    const db = await this.initDB();
    
    let audioBlob: Blob | undefined;
    
    // Convert blob URL to actual blob for storage
    if (transcription.audioUrl) {
      try {
        console.log('Fetching audio blob from URL:', transcription.audioUrl);
        const response = await fetch(transcription.audioUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
        }
        audioBlob = await response.blob();
        console.log('Audio blob created:', audioBlob.type, audioBlob.size, 'bytes');
      } catch (error) {
        console.error('Failed to fetch audio blob:', error);
      }
    }

    const record: StoredTranscriptionRecord = {
      id: this.generateId(),
      timestamp: new Date(),
      text: transcription.text,
      language: transcription.language,
      duration: transcription.duration,
      isRealTime: transcription.isRealTime,
      segments: transcription.segments,
      audioBlob
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.TRANSCRIPTION_STORE], 'readwrite');
      const store = transaction.objectStore(this.TRANSCRIPTION_STORE);
      
      const request = store.add(record);
      
      request.onsuccess = async () => {
        // Clean up old records to maintain MAX_RECORDS limit
        await this.cleanupOldRecords();
        
        // Return record with audioUrl for immediate use
        const returnRecord: TranscriptionRecord = {
          ...record,
          audioUrl: audioBlob ? URL.createObjectURL(audioBlob) : undefined
        };
        
        if (returnRecord.audioUrl) {
          this.blobUrls.add(returnRecord.audioUrl);
        }
        
        resolve(returnRecord);
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  // Get all transcriptions (without creating blob URLs immediately)
  async getAllTranscriptions(): Promise<TranscriptionRecord[]> {
    try {
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.TRANSCRIPTION_STORE], 'readonly');
        const store = transaction.objectStore(this.TRANSCRIPTION_STORE);
        const index = store.index('timestamp');
        
        // Get all records ordered by timestamp (newest first)
        const request = index.openCursor(null, 'prev');
        const records: TranscriptionRecord[] = [];
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            const storedRecord = cursor.value as StoredTranscriptionRecord;
            
            // Don't create blob URLs here - we'll create them on demand
            const record: TranscriptionRecord = {
              ...storedRecord,
              audioUrl: undefined // Will be created when needed
            };
            
            records.push(record);
            cursor.continue();
          } else {
            resolve(records);
          }
        };
        
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error loading transcriptions:', error);
      return [];
    }
  }

  // Get audio blob URL for a specific transcription
  async getAudioUrl(transcriptionId: string): Promise<string | null> {
    try {
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.TRANSCRIPTION_STORE], 'readonly');
        const store = transaction.objectStore(this.TRANSCRIPTION_STORE);
        
        const request = store.get(transcriptionId);
        
        request.onsuccess = () => {
          const storedRecord = request.result as StoredTranscriptionRecord;
          if (storedRecord?.audioBlob) {
            try {
              const audioUrl = URL.createObjectURL(storedRecord.audioBlob);
              this.blobUrls.add(audioUrl);
              console.log('Created fresh blob URL for transcription:', transcriptionId, 'Blob type:', storedRecord.audioBlob.type, 'Size:', storedRecord.audioBlob.size);
              resolve(audioUrl);
            } catch (error) {
              console.error('Failed to create blob URL for transcription:', transcriptionId, error);
              resolve(null);
            }
          } else {
            resolve(null);
          }
        };
        
        request.onerror = () => {
          console.error('Failed to get transcription:', transcriptionId, request.error);
          resolve(null);
        };
      });
    } catch (error) {
      console.error('Error getting audio URL:', error);
      return null;
    }
  }

  // Delete a transcription
  async deleteTranscription(id: string): Promise<void> {
    try {
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.TRANSCRIPTION_STORE], 'readwrite');
        const store = transaction.objectStore(this.TRANSCRIPTION_STORE);
        
        const request = store.delete(id);
        
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error deleting transcription:', error);
      throw error;
    }
  }

  // Clear all transcriptions
  async clearAll(): Promise<void> {
    try {
      // Clean up blob URLs
      this.blobUrls.forEach(url => URL.revokeObjectURL(url));
      this.blobUrls.clear();
      
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.TRANSCRIPTION_STORE], 'readwrite');
        const store = transaction.objectStore(this.TRANSCRIPTION_STORE);
        
        const request = store.clear();
        
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error clearing transcriptions:', error);
      throw error;
    }
  }

  // Clean up old records to maintain MAX_RECORDS limit
  private async cleanupOldRecords(): Promise<void> {
    try {
      const db = await this.initDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.TRANSCRIPTION_STORE], 'readwrite');
        const store = transaction.objectStore(this.TRANSCRIPTION_STORE);
        const index = store.index('timestamp');
        
        // Get all records ordered by timestamp (oldest first)
        const request = index.openCursor(null, 'next');
        const recordsToDelete: string[] = [];
        let count = 0;
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            count++;
            if (count > this.MAX_RECORDS) {
              recordsToDelete.push(cursor.value.id);
            }
            cursor.continue();
          } else {
            // Delete old records
            const deletePromises = recordsToDelete.map(id => {
              return new Promise<void>((deleteResolve, deleteReject) => {
                const deleteRequest = store.delete(id);
                deleteRequest.onsuccess = () => deleteResolve();
                deleteRequest.onerror = () => deleteReject(deleteRequest.error);
              });
            });
            
            Promise.all(deletePromises)
              .then(() => resolve())
              .catch(reject);
          }
        };
        
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error cleaning up old records:', error);
    }
  }

  // Clean up blob URLs (call when component unmounts)
  cleanup(): void {
    this.blobUrls.forEach(url => URL.revokeObjectURL(url));
    this.blobUrls.clear();
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}

export const transcriptionStorage = new TranscriptionStorage();

// Language-specific placeholder messages
export const getPlaceholderMessage = (language: string): string => {
  const uiLang = getUILanguage(language);
  const t = getTranslations(uiLang);
  return t.placeholders[language as keyof typeof t.placeholders] || t.placeholders['en-US'];
};